import type { Material, Object3D } from 'three';
import { isMeshLike } from '../../../3rd-party/types/is-threejs-type';
import type { ThreeJsMeshLike } from '../../../3rd-party/types/three-js-mesh-like';
import type { ThreeJsMaterialLike } from '../../../3rd-party/types/three-js-material-like';

export function disposeMaterial(material: ThreeJsMaterialLike | undefined): void {
  if (!material) {
    return;
  }
  if (material.map) {
    material.map.dispose();
  }
  if (material.lightMap) {
    material.lightMap.dispose();
  }
  if (material.bumpMap) {
    material.bumpMap.dispose();
  }
  if (material.normalMap) {
    material.normalMap.dispose();
  }
  if (material.specularMap) {
    material.specularMap.dispose();
  }
  if (material.envMap) {
    material.envMap.dispose();
  }
  material.dispose();
}

export function storeOriginalMaterialRecursively(object: Object3D): void {
  storeOriginalMaterial(object);
  for (const child of object.children) {
    storeOriginalMaterialRecursively(child);
  }
}

export function storeOriginalMaterial(object: Object3D): void {
  const mesh = object as ThreeJsMeshLike;
  if (!mesh.material) {
    return;
  }
  if (Array.isArray(mesh.material)) {
    object.userData.originalMaterial = [];
    for (const material of mesh.material) {
      object.userData.originalMaterial.push(material.clone());
    }
  } else {
    object.userData.originalMaterial = mesh.material.clone();
  }
}

export function restoreOriginalMaterialRecursively(object: Object3D): void {
  restoreOriginalMaterial(object);
  for (const child of object.children) {
    restoreOriginalMaterialRecursively(child);
  }
}

export function restoreOriginalMaterial(object: Object3D): void {
  if (!object.userData.originalMaterial) {
    return;
  }
  if (Array.isArray(object.userData.originalMaterial)) {
    const materials = object.userData.originalMaterial.map(m => m.clone());
    (object as ThreeJsMeshLike).material = materials;
  } else {
    (object as ThreeJsMeshLike).material = object.userData.originalMaterial.clone();
  }
}

function setArrayMaterialPreservingColorsFromOriginalMaterial(object: Object3D, material: Material): void {
  const mesh = object as ThreeJsMeshLike;
  if (!mesh.material) {
    return;
  }
  for (let i = 0; i < (mesh.material as Material[]).length; i++) {
    const newMaterial = material.clone() as ThreeJsMaterialLike;
    newMaterial.color = object.userData.originalMaterial[i].color.clone();
    newMaterial.map = object.userData.originalMaterial[i].map;
    mesh.material[i] = newMaterial;
  }
}

function setSingleMaterialPreservingColorsFromOriginalMaterial(object: Object3D, material: Material): void {
  const mesh = object as ThreeJsMeshLike;
  if (!object.userData.originalMaterial) {
    return;
  }
  const newMaterial = material.clone() as ThreeJsMaterialLike;
  newMaterial.color = object.userData.originalMaterial.color.clone();
  newMaterial.map = object.userData.originalMaterial.map;
  mesh.material = newMaterial;
}

function setMaterialClone(object: ThreeJsMeshLike, material: Material): void {
  if (Array.isArray(object.material)) {
    for (let i = 0; i < object.material.length; i++) {
      object.material[i] = material.clone();
    }
  } else {
    object.material = material.clone();
  }
}

export function setMaterial(object: Object3D, material: Material, preserveColor: boolean): void {
  if (!isMeshLike(object)) {
    return;
  }

  if (preserveColor) {
    if (Array.isArray(object.material)) {
      setArrayMaterialPreservingColorsFromOriginalMaterial(object, material);
    } else {
      setSingleMaterialPreservingColorsFromOriginalMaterial(object, material);
    }
  } else {
    setMaterialClone(object, material);
  }
}

export function setMaterialRecursively(object: Object3D, material: Material, preserveColor: boolean): void {
  setMaterial(object, material, preserveColor);
  for (const child of object.children) {
    setMaterialRecursively(child, material, preserveColor);
  }
}

export function setLinearColorRecursively(object: Object3D): void {
  const anyObject = object as ThreeJsMeshLike;
  if (!anyObject.geometry || !anyObject.material) {
    return;
  }

  const materials = Array.isArray(anyObject.material) ? anyObject.material : [anyObject.material];

  for (const material of materials) {
    material.color.convertSRGBToLinear();
  }
}
